import { useEffect, useState } from 'react'

import { Form, Select, Space, Input } from 'antd'
import { useForm } from 'antd/es/form/Form'

import i18n from 'i18next'

import CommonModal from '@/components/common/CommonModal'
import Info from '@/components/common/Info'
import MultiSelectDropdown from '@/components/common/MultiSelectDropdown'

import { ActionProps, ActionTypes } from './types'

export type SumValuesForm = {
  axis: 0 | 1
  by: number[]
  output_name: string
}

const SumValues = ({
  columns = [],
  rows = [],
  onSubmit,
  onCancel,
  isVisible,
  initialValues,
  clearOptions = false
}: ActionProps<any>) => {
  const initial = {
    axis: 1, // default: columns
    by: [],
    output_name: '',
    ...(initialValues || {})
  }
  const [form] = useForm<SumValuesForm>()
  const [byLength, setByLength] = useState(initial.by.length)
  const [labelsVisible, setLabelsVisible] = useState(false)
  const [outputName, setOutputName] = useState(initial.output_name)

  useEffect(() => {
    if (clearOptions === true) {
      setByLength(initial.by.length)
      form.resetFields()
      //@ts-ignore
      form.setFieldsValue(initial)
      onByChange(initial.by)
    }
  }, [clearOptions, initialValues])

  useEffect(() => {
    form.resetFields()
  }, [])

  const submitForm = () => {
    form.submit()
    const { axis, by, output_name } = form.getFieldsValue(true)
    const payload = {
      actionType: ActionTypes.SUM_VALUES,
      type: ActionTypes.SUM_VALUES,
      axis,
      by,
      specs: {
        output_name
      }
    }
    onSubmit?.(payload)
  }

  const onAxisChange = (value: 0 | 1) => {
    form.setFieldsValue({ axis: value, by: [] })
    setByLength(0)
  }

  const onByChange = (value: number[]) => {
    form.setFieldsValue({ by: value })
    setByLength(form.getFieldsValue(true).by.length)
  }

  const onFieldsChange = (changedFields: any[]) => {
    const outputNameField = changedFields.find(field => field.name[0] === 'output_name')
    if (outputNameField) {
      setOutputName(outputNameField.value || '')
    }
  }

  // Handle initial values from edit mode
  useEffect(() => {
    if (initialValues?.specs?.output_name) {
      setOutputName(initialValues.specs.output_name)
    }
  }, [initialValues])

  useEffect(() => {
    setByLength(initial.by.length)
  }, [initialValues])

  const popoverContent = (
    <div>
      <strong>Function</strong>
      <p>
        Sum the values of selected columns or rows and output as a new column or row.
      </p>
      <strong>How to use</strong>
      <p>[Orientation]: Select if you want to sum columns or rows.</p>
      <p>[To sum]: Select at least 2 columns or rows to sum.</p>
      <p>[Name output]: Enter a name for the new column or row (max 250 characters).</p>
      <strong>NOTES</strong>
      <p>
        <em>
          All fields are required. You must select at least 2 columns or rows to sum.
        </em>
      </p>
    </div>
  )

  return (
    <CommonModal
      visible={isVisible}
      onCancel={onCancel}
      onOk={submitForm}
      okDisabled={!outputName || byLength < 2}
      title={
        <span>
          Sum values <Info>{popoverContent}</Info>
        </span>
      }
    >
      <Form form={form} initialValues={initial} layout="vertical" onFieldsChange={onFieldsChange}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Form.Item
            name="axis"
            rules={[{ required: true }]}
            label="Orientation"
          >
            <Select onChange={onAxisChange}>
              <Select.Option value={1}>Columns</Select.Option>
              <Select.Option value={0}>Rows</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            rules={[{ required: true }]}
            label="To sum"
            shouldUpdate={(prevValues, curValues) => prevValues.axis !== curValues.axis}
          >
            {({ getFieldValue }) => {
              const axis = getFieldValue('axis')
              const options = axis === 0 ? rows : columns
              return (
                <Form.Item name="by" initialValue={initial.by} rules={[
                  { required: true, message: 'Please select at least 2 items to sum.' },
                  { validator: (_, value) => (value && value.length >= 2 ? Promise.resolve() : Promise.reject('Select at least 2')) }
                ]}>
                  <MultiSelectDropdown
                    onChange={onByChange}
                    visible={labelsVisible}
                    setVisible={setLabelsVisible}
                    dropdownSelect={() => {
                      form.setFieldsValue({ by: options.map(({ index }) => index) })
                      setByLength(form.getFieldsValue(true).by.length)
                    }}
                    dropdownClear={() => {
                      form.setFieldsValue({ by: [] })
                      setByLength(0)
                    }}
                    dropdownOk={() => setLabelsVisible(false)}
                    dropdownCancel={() => {
                      form.setFieldsValue({ by: [] })
                      setLabelsVisible(false)
                    }}
                  >
                    {options.map(({ index, name }) => (
                      <Select.Option value={index} key={index}>
                        {name}
                      </Select.Option>
                    ))}
                  </MultiSelectDropdown>
                </Form.Item>
              )
            }}
          </Form.Item>

          <Form.Item
            name="output_name"
            label="Name output"
            rules={[
              { required: true, message: 'Please enter a name for the output.' },
              { max: 250, message: 'Name must be at most 250 characters.' }
            ]}
          >
            <Input maxLength={250} placeholder="Enter output name" />
          </Form.Item>
        </Space>
      </Form>
    </CommonModal>
  )
}

export default SumValues 